import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { centerCostAPI } from '../api/client';
import { alertDialog, confirmDialog } from '../components/AppDialog';
import { Page, PageHeader, PillTabs, SectionTitle, Button, SearchInput, Card, EmptyState } from '../components/ui';
import { COLORS, TONES, ICON_STROKE, input as uiInput } from '../utils/uiStyles';
import { IconBuildingWarehouse, IconChevronLeft, IconChevronRight, IconChevronDown, IconChevronsLeft, IconChevronsRight } from '@tabler/icons-react';
import { kstToday } from '../utils/kstTime';

/* 센터 전용 — 일일 비용자료 등록/조회.
 * 원본 보관은 유지하고, 전 센터가 같은 양식을 쓰는 더존 소모품비만 명세를 함께 보여 준다.
 * 나머지 비용은 센터마다 양식이 달라 원본을 그대로 보관한다.
 */
// 마스터 지정 4종. **업로드 시 반드시 하나를 고른다**(2026-08-18) — '기타'는 두지 않는다.
const COST_TYPES = [
  { key: 'supplies', label: '소모품비' },
  { key: 'labor', label: '인건비' },
  { key: 'transport', label: '운송료' },
  { key: 'storage', label: '보관비' },
];
const TYPE_LABEL = Object.fromEntries(COST_TYPES.map((t) => [t.key, t.label]));
const PAGE_SIZES = [20, 50, 100];
const SUPPLY_DETAIL_SIZE = 100;

// 🔴 KST 기준 오늘 — UTC 날짜로 만들면 KST 00~09시에 등록 기본값이 어제가 된다(서버도 kst.today()).
const today = () => kstToday();
const fmtSize = (n) => (!n && n !== 0 ? '-' : n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
const pad2 = (n) => String(n).padStart(2, '0');
const currentMonth = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`; };
const monthRange = (month) => {
  const [year, value] = String(month).split('-').map(Number);
  return {
    start: `${year}-${pad2(value)}-01`,
    end: `${year}-${pad2(value)}-${pad2(new Date(year, value, 0).getDate())}`,
  };
};
const shiftMonth = (month, offset) => {
  const [year, value] = String(month).split('-').map(Number);
  const d = new Date(year, value - 1 + offset, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};
const fmtNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const [whole, fraction] = String(value).split('.');
  const formatted = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction ? `${formatted}.${fraction}` : formatted;
};

const EMPTY_SUPPLIES = {
  months: [], groups: [],
  totals: { count: 0, qty: '0', supply_amount: '0', vat: '0', total_amount: '0' },
};
const mergeSupplyData = (responses) => {
  const amountFields = ['qty', 'supply_amount', 'vat', 'total_amount'];
  const totals = { count: 0, qty: 0, supply_amount: 0, vat: 0, total_amount: 0 };
  const groups = new Map();
  const months = new Set();
  responses.forEach((data) => {
    (data?.months || []).forEach((month) => months.add(month));
    totals.count += Number(data?.totals?.count || 0);
    amountFields.forEach((field) => { totals[field] += Number(data?.totals?.[field] || 0); });
    (data?.groups || []).forEach((group) => {
      const merged = groups.get(group.key) || {
        ...group, count: 0, qty: 0, supply_amount: 0, vat: 0, total_amount: 0,
      };
      merged.count += Number(group.count || 0);
      amountFields.forEach((field) => { merged[field] += Number(group[field] || 0); });
      groups.set(group.key, merged);
    });
  });
  return {
    months: [...months].sort().reverse(),
    groups: [...groups.values()]
      .map((group) => ({
        ...group,
        ratio: totals.total_amount ? Math.round((group.total_amount / totals.total_amount) * 10000) / 100 : 0,
      }))
      .sort((a, b) => b.total_amount - a.total_amount || String(a.key).localeCompare(String(b.key), 'ko')),
    totals,
  };
};

export default function CenterCostPage() {
  const { token } = useParams();
  const center = decodeURIComponent(token || '');
  const [name, setName] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // 소모품비 명세 — 월말 자료이므로 오늘까지가 아닌 이번 달 말일까지가 기본 범위다.
  const initialMonth = currentMonth();
  const initialRange = monthRange(initialMonth);
  const [supplyMonth, setSupplyMonth] = useState(initialMonth);
  const [supplyStart, setSupplyStart] = useState(initialRange.start);
  const [supplyEnd, setSupplyEnd] = useState(initialRange.end);
  const [directRange, setDirectRange] = useState(false);
  const [supplyTab, setSupplyTab] = useState('vendor');
  const [supplyCenterOptions, setSupplyCenterOptions] = useState([{ token: center, name: center }]);
  const [selectedSupplyCenters, setSelectedSupplyCenters] = useState([center]);
  const [supplyVendorOptions, setSupplyVendorOptions] = useState([]);
  const [selectedSupplyVendors, setSelectedSupplyVendors] = useState([]);
  const [supplyData, setSupplyData] = useState(EMPTY_SUPPLIES);
  const [supplyLoading, setSupplyLoading] = useState(true);
  const [supplyError, setSupplyError] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailCursors, setDetailCursors] = useState({});
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [uploadReport, setUploadReport] = useState(null);
  const [supplyReload, setSupplyReload] = useState(0);
  const detailRequestRef = useRef(0);

  // 등록 폼
  const [costDate, setCostDate] = useState(today());
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState([]);           // [{file, type}]
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    centerCostAPI.list(center, start, end)
      .then((r) => { setName(r.data?.name || center); setEntries(r.data?.entries || []); setDenied(false); })
      .catch((e) => { if (e?.response?.status === 403) setDenied(true); setEntries([]); })
      .finally(() => setLoading(false));
  }, [center, start, end]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [q, start, end]);

  useEffect(() => {
    setUploadReport(null);
    setSelectedSupplyCenters([center]);
    setSelectedSupplyVendors([]);
  }, [center]);

  useEffect(() => {
    let active = true;
    centerCostAPI.myCenters()
      .then((r) => {
        if (!active) return;
        const accessible = r.data?.centers || [];
        setSupplyCenterOptions(accessible.some((item) => item.token === center)
          ? accessible
          : [{ token: center, name: center }, ...accessible]);
      })
      .catch(() => { if (active) setSupplyCenterOptions([{ token: center, name: center }]); });
    return () => { active = false; };
  }, [center]);

  useEffect(() => {
    let active = true;
    setSupplyVendorOptions([]);
    if (!selectedSupplyCenters.length || !supplyStart || !supplyEnd || supplyStart > supplyEnd) {
      setSelectedSupplyVendors([]);
      return () => { active = false; };
    }
    Promise.all(selectedSupplyCenters.map((selectedCenter) => centerCostAPI.supplies(selectedCenter, {
      start: supplyStart, end: supplyEnd, groupBy: 'vendor',
    })))
      .then((responses) => {
        if (!active) return;
        const options = mergeSupplyData(responses.map((r) => r.data || EMPTY_SUPPLIES)).groups
          .map((group) => ({ key: group.key, label: group.label || group.key }));
        const keys = new Set(options.map((option) => option.key));
        setSupplyVendorOptions(options);
        setSelectedSupplyVendors((selected) => selected.filter((vendor) => keys.has(vendor)));
      })
      .catch(() => {
        if (!active) return;
        setSupplyVendorOptions([]);
        setSelectedSupplyVendors([]);
      });
    return () => { active = false; };
  }, [selectedSupplyCenters, supplyStart, supplyEnd, supplyReload]);

  useEffect(() => {
    let active = true;
    detailRequestRef.current += 1;
    setExpandedKey(null);
    setDetailRows([]);
    setDetailCursors({});
    setDetailTotal(0);
    setDetailError('');
    if (!supplyStart || !supplyEnd || supplyStart > supplyEnd) {
      setSupplyLoading(false);
      setSupplyError('시작일은 종료일보다 늦을 수 없습니다.');
      setSupplyData(EMPTY_SUPPLIES);
      return () => { active = false; };
    }
    if (!selectedSupplyCenters.length) {
      setSupplyLoading(false);
      setSupplyError('');
      setSupplyData(EMPTY_SUPPLIES);
      return () => { active = false; };
    }
    setSupplyLoading(true);
    setSupplyError('');
    Promise.all(selectedSupplyCenters.map((selectedCenter) => centerCostAPI.supplies(selectedCenter, {
      start: supplyStart, end: supplyEnd, groupBy: supplyTab, vendors: selectedSupplyVendors,
    })))
      .then((responses) => { if (active) setSupplyData(mergeSupplyData(responses.map((r) => r.data || EMPTY_SUPPLIES))); })
      .catch((e) => {
        if (!active) return;
        setSupplyData(EMPTY_SUPPLIES);
        setSupplyError(e?.response?.data?.detail || '소모품비 명세를 불러오지 못했습니다.');
      })
      .finally(() => { if (active) setSupplyLoading(false); });
    return () => { active = false; };
  }, [selectedSupplyCenters, selectedSupplyVendors, supplyStart, supplyEnd, supplyTab, supplyReload]);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    setPicked((p) => [...p, ...arr.map((f) => ({ file: f, type: '' }))].slice(0, 10));
  };
  const missingType = picked.some((p) => !p.type);
  const submit = async () => {
    if (!picked.length) { alertDialog('첨부 파일을 1개 이상 선택해 주세요.'); return; }
    if (missingType) { alertDialog('첨부마다 비용 항목(소모품비·인건비·운송료·보관비)을 선택해 주세요.'); return; }
    const fd = new FormData();
    fd.append('center', center);
    fd.append('cost_date', costDate);
    fd.append('note', note);
    fd.append('cost_types', picked.map((p) => p.type).join(','));
    picked.forEach((p) => fd.append('files', p.file));
    setSaving(true);
    setUploadReport(null);
    try {
      const result = await centerCostAPI.create(fd);
      setUploadReport(result.data?.supply_report || null);
      setPicked([]); setNote('');
      if (fileRef.current) fileRef.current.value = '';
      load();
      setSupplyReload((value) => value + 1);
    } catch (e) {
      alertDialog(e?.response?.data?.detail || '등록에 실패했습니다.');
    } finally { setSaving(false); }
  };
  const remove = async (e) => {
    if (!(await confirmDialog(`${e.cost_date} 자료를 삭제하시겠습니까? 첨부 파일도 함께 지워집니다.`, { danger: true }))) return;
    try { await centerCostAPI.remove(e.id); load(); setSupplyReload((value) => value + 1); }
    catch (err) { alertDialog(err?.response?.data?.detail || '삭제에 실패했습니다.'); }
  };
  const download = async (f) => {
    try {
      const r = await centerCostAPI.download(f.id);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = f.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (err) { alertDialog(err?.response?.data?.detail || '내려받기에 실패했습니다.'); }
  };

  const selectMonth = (nextMonth) => {
    const range = monthRange(nextMonth);
    setSupplyMonth(nextMonth);
    setSupplyStart(range.start);
    setSupplyEnd(range.end);
    setDirectRange(false);
  };
  const toggleDirectRange = () => {
    if (directRange) {
      const range = monthRange(supplyMonth);
      setSupplyStart(range.start);
      setSupplyEnd(range.end);
    }
    setDirectRange((value) => !value);
  };
  const loadDetails = async (key, append = false) => {
    const centersToLoad = append
      ? selectedSupplyCenters.filter((selectedCenter) => detailCursors[selectedCenter]?.hasMore)
      : selectedSupplyCenters;
    if (!centersToLoad.length) return;
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setDetailLoading(true);
    setDetailError('');
    try {
      const responses = await Promise.all(centersToLoad.map(async (selectedCenter) => {
        const cursor = append ? detailCursors[selectedCenter] : null;
        const response = await centerCostAPI.supplies(selectedCenter, {
          start: supplyStart,
          end: supplyEnd,
          groupBy: supplyTab,
          vendors: selectedSupplyVendors,
          detailKey: key,
          detailOffset: cursor?.offset || 0,
          detailLimit: SUPPLY_DETAIL_SIZE,
        });
        return { center: selectedCenter, data: response.data || EMPTY_SUPPLIES };
      }));
      if (detailRequestRef.current !== requestId) return;
      const nextCursors = append ? { ...detailCursors } : {};
      const nextRows = [];
      responses.forEach(({ center: selectedCenter, data }) => {
        const pageData = data.detail_page || {};
        const details = data.details || [];
        nextRows.push(...details);
        nextCursors[selectedCenter] = {
          offset: Number(pageData.offset || 0) + details.length,
          total: Number(pageData.total || details.length),
          hasMore: !!pageData.has_more,
        };
      });
      const combined = append ? [...detailRows, ...nextRows] : nextRows;
      combined.sort((a, b) => String(a.recv_date).localeCompare(String(b.recv_date))
        || String(a.recv_no).localeCompare(String(b.recv_no), 'ko')
        || Number(a.line_no || 0) - Number(b.line_no || 0));
      setDetailRows(combined);
      setDetailCursors(nextCursors);
      setDetailTotal(Object.values(nextCursors).reduce((sum, cursor) => sum + cursor.total, 0));
    } catch (e) {
      if (detailRequestRef.current !== requestId) return;
      if (!append) {
        setDetailRows([]);
        setDetailCursors({});
        setDetailTotal(0);
      }
      setDetailError(e?.response?.data?.detail || '원본 명세를 불러오지 못했습니다.');
    } finally {
      if (detailRequestRef.current === requestId) setDetailLoading(false);
    }
  };
  const toggleDetails = (key) => {
    if (expandedKey === key) {
      detailRequestRef.current += 1;
      setExpandedKey(null);
      setDetailRows([]);
      setDetailCursors({});
      setDetailTotal(0);
      setDetailError('');
      setDetailLoading(false);
      return;
    }
    setExpandedKey(key);
    setDetailRows([]);
    setDetailCursors({});
    setDetailTotal(0);
    setDetailError('');
    loadDetails(key);
  };

  const shown = entries.filter((e) => {
    const k = q.trim().toLowerCase();
    if (!k) return true;
    return (e.note || '').toLowerCase().includes(k)
      || (e.uploader_name || '').toLowerCase().includes(k)
      || (e.files || []).some((f) => (f.name || '').toLowerCase().includes(k));
  });
  const totalPages = Math.max(1, Math.ceil(shown.length / perPage));
  const curPage = Math.min(page, totalPages);
  const rows = shown.slice((curPage - 1) * perPage, curPage * perPage);
  const availableMonths = Array.from(new Set([supplyMonth, ...(supplyData.months || [])])).sort().reverse();
  const supplyGroups = supplyData.groups || [];
  const supplyTotals = supplyData.totals || EMPTY_SUPPLIES.totals;
  const detailHasMore = Object.values(detailCursors).some((cursor) => cursor.hasMore);

  const input = { ...uiInput, width: 'auto' };
  const th = { textAlign: 'left', height: '33px', padding: '5px 9px', borderBottom: `1px solid ${COLORS.borderSoft}`, color: COLORS.textMuted, fontWeight: 400, fontSize: '12px', whiteSpace: 'nowrap' };
  // 각 표의 첫 열 — 값은 th/td 와 같고 왼쪽 패딩만 4(시안 .database :first-child). shorthand 와 paddingLeft 를 한 객체에 섞지 않는다.
  const thFirst = { textAlign: 'left', height: '33px', paddingTop: '5px', paddingRight: '9px', paddingBottom: '5px', paddingLeft: '4px', borderBottom: `1px solid ${COLORS.borderSoft}`, color: COLORS.textMuted, fontWeight: 400, fontSize: '12px', whiteSpace: 'nowrap' };
  const td = { height: '41px', padding: '7px 9px', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '13px', color: COLORS.textSub, verticalAlign: 'middle', wordBreak: 'break-word' };
  const tdFirst = { height: '41px', paddingTop: '7px', paddingRight: '9px', paddingBottom: '7px', paddingLeft: '4px', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '13px', color: COLORS.text, fontWeight: 500, verticalAlign: 'middle', wordBreak: 'break-word' };
  // 등록 원본 표 — 첨부가 여러 줄이라 칸 높이가 들쭉날쭉해 위 정렬 유지
  const tdTop = { ...td, verticalAlign: 'top' };
  const chevron = { verticalAlign: 'middle', marginRight: '4px' };

  return (
    <AppShell app={`center:${center}`}>
      {/* 본문은 사이드바의 형제다 — AppSidebar 의 children 은 사이드바 아코디언용이라
          거기에 넣으면 화면이 통째로 비어 보인다(레포 규약: DirectoryPage 참조). */}
      <div style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto' }}>
      <Page>
        <PageHeader
          icon={<IconBuildingWarehouse aria-hidden="true" />}
          title={`${name || center} · 일일 비용자료`}
          subtitle={`소모품비 · 인건비 · 운송료 · 보관비를 날짜별로 올려 주세요. 파일마다 어느 비용인지 선택해야 등록됩니다. 원본은 양식 그대로 보관하며, 더존 iCube 소모품비 파일은 아래에서 자동 집계합니다. 이 메뉴는 ${name || center} 인원과 AX실·관리자만 볼 수 있습니다.`}
        />

        {denied ? (
          <div style={{ padding: '40px', textAlign: 'center', color: COLORS.text, background: COLORS.warnSoft, borderRadius: '10px' }}>
            이 센터 자료에 접근할 권한이 없습니다.
          </div>
        ) : (
          <>
            {/* ── 등록 ── */}
            <Card padding="16px" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: 500, color: COLORS.textSub }}>일자</label>
                <input type="date" value={costDate} max={today()} onChange={(e) => setCostDate(e.target.value)} style={input} />
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="메모(선택)" style={{ ...input, flex: 1, minWidth: '200px' }} />
                <input ref={fileRef} type="file" multiple onChange={(e) => addFiles(e.target.files)} style={{ fontSize: '13px', color: COLORS.text }} />
              </div>
              {picked.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                  {picked.map((p, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.file.name}</span>
                      <span style={{ color: COLORS.textMuted }}>{fmtSize(p.file.size)}</span>
                      <select value={p.type} onChange={(ev) => setPicked((arr) => arr.map((x, j) => (j === i ? { ...x, type: ev.target.value } : x)))}
                        title="이 파일이 어느 비용인지 고릅니다(필수)."
                        style={{ ...input, padding: '4px 8px', fontSize: '12px',
                          border: p.type ? `1px solid ${COLORS.border}` : `1px solid ${TONES.yellow.ink}`,
                          background: p.type ? COLORS.surface : COLORS.warnSoft }}>
                        <option value="">항목 선택 *</option>
                        {COST_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                      </select>
                      <button onClick={() => setPicked((arr) => arr.filter((_, j) => j !== i))}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: COLORS.dangerText, fontSize: '13px' }}>제거</button>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Button variant="primary" onClick={submit} disabled={saving || !picked.length || missingType}>
                  {saving ? '올리는 중…' : '등록'}
                </Button>
                {picked.length > 0 && missingType && (
                  <span style={{ fontSize: '12px', color: COLORS.warn }}>첨부마다 비용 항목을 선택해야 등록됩니다.</span>
                )}
              </div>
            </Card>

            {uploadReport && (
              <div role="status" style={{ padding: '12px 14px', margin: '-10px 0 20px', border: `1px solid ${COLORS.borderSoft}`, borderRadius: '8px', background: COLORS.successSoft, color: COLORS.successText, fontSize: '13px', lineHeight: 1.7 }}>
                <b>소모품비 명세 적재 완료</b><br />
                적재 {fmtNumber(uploadReport.rows)}건 · 합계 {fmtNumber(uploadReport.total_amount)}원 (검산 일치 ✓)<br />
                기간 {uploadReport.start} ~ {uploadReport.end} · 센터 {fmtNumber(uploadReport.centers)} · 거래처 {fmtNumber(uploadReport.vendors)}
                {uploadReport.unmatched_count > 0 && (
                  <div style={{ color: COLORS.text, fontWeight: 500 }}>
                    미매칭 센터 {fmtNumber(uploadReport.unmatched_count)}건 · {(uploadReport.unmatched_centers || []).map((item) => `${item.name} ${item.count}건`).join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* ── 소모품비 명세 ── */}
            <Card padding="16px" style={{ marginBottom: '20px' }}>
            <section aria-labelledby="supply-cost-heading">
              <h2 id="supply-cost-heading" style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: 620, color: COLORS.text }}>소모품비 명세</h2>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                <Button variant="secondary" icon={<IconChevronLeft aria-hidden="true" />} aria-label="이전 달" onClick={() => selectMonth(shiftMonth(supplyMonth, -1))} />
                <select aria-label="소모품비 조회 월" value={supplyMonth} onChange={(e) => selectMonth(e.target.value)} style={{ ...input, fontWeight: 500 }}>
                  {availableMonths.map((month) => <option key={month} value={month}>{month}</option>)}
                </select>
                <Button variant="secondary" icon={<IconChevronRight aria-hidden="true" />} aria-label="다음 달" onClick={() => selectMonth(shiftMonth(supplyMonth, 1))} />
                <Button variant={directRange ? 'primary' : 'secondary'} aria-expanded={directRange} aria-controls="supply-direct-range" onClick={toggleDirectRange}>
                  기간 직접 지정
                </Button>
                {(supplyData.months || []).length > 0 && (
                  <span style={{ fontSize: '12px', color: COLORS.textSub }}>적재 월 {(supplyData.months || []).join(', ')}</span>
                )}
              </div>
              {directRange && (
                <div id="supply-direct-range" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                  <label htmlFor="supply-start" style={{ fontSize: '13px', color: COLORS.textSub }}>입고일자</label>
                  <input id="supply-start" aria-label="소모품비 시작일" type="date" value={supplyStart} onChange={(e) => setSupplyStart(e.target.value)} style={input} />
                  <span style={{ color: COLORS.textMuted }}>~</span>
                  <input aria-label="소모품비 종료일" type="date" value={supplyEnd} onChange={(e) => setSupplyEnd(e.target.value)} style={input} />
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '12px' }}>
                <label style={{ display: 'grid', gap: '4px', fontSize: '12px', fontWeight: 500 }}>
                  센터 다중 선택
                  <select multiple size={Math.min(9, Math.max(2, supplyCenterOptions.length))} aria-label="소모품비 센터 다중 선택"
                    value={selectedSupplyCenters}
                    onChange={(e) => setSelectedSupplyCenters(Array.from(e.target.selectedOptions, (option) => option.value))}
                    style={{ ...input, minWidth: '210px', background: COLORS.surface }}>
                    {supplyCenterOptions.map((item) => <option key={item.token} value={item.token}>{item.name || item.token}</option>)}
                  </select>
                </label>
                <label style={{ display: 'grid', gap: '4px', fontSize: '12px', fontWeight: 500 }}>
                  거래처 다중 선택 <span style={{ color: COLORS.textSub, fontWeight: 400 }}>(미선택 시 전체)</span>
                  <select multiple size={Math.min(9, Math.max(2, supplyVendorOptions.length))} aria-label="소모품비 거래처 다중 선택"
                    value={selectedSupplyVendors}
                    onChange={(e) => setSelectedSupplyVendors(Array.from(e.target.selectedOptions, (option) => option.value))}
                    style={{ ...input, minWidth: '280px', background: COLORS.surface }}>
                    {supplyVendorOptions.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                  </select>
                </label>
                <span style={{ alignSelf: 'center', color: COLORS.textSub, fontSize: '12px' }}>
                  센터 {selectedSupplyCenters.length}곳 · 거래처 {selectedSupplyVendors.length || '전체'}
                </span>
              </div>

              <PillTabs
                idBase="supply"
                ariaLabel="소모품비 집계 기준"
                items={[{ key: 'vendor', label: '거래처별' }, { key: 'item', label: '품목별' }]}
                value={supplyTab}
                onChange={setSupplyTab}
              />

              {supplyError ? (
                <div role="alert" style={{ padding: '24px', color: COLORS.dangerText, textAlign: 'center' }}>{supplyError}</div>
              ) : supplyLoading ? (
                <div style={{ padding: '24px', color: COLORS.textMuted }}>소모품비 명세를 불러오는 중…</div>
              ) : selectedSupplyCenters.length === 0 ? (
                <EmptyState description="조회할 센터를 1개 이상 선택해 주세요." />
              ) : supplyGroups.length === 0 ? (
                <EmptyState description="선택한 기간에 적재된 소모품비 명세가 없습니다." />
              ) : (
                <div role="tabpanel" id={`supply-panel-${supplyTab}`} aria-label={`${supplyTab === 'vendor' ? '거래처별' : '품목별'} 소모품비 집계`} style={{ maxHeight: '620px', overflow: 'auto' }}>
                  <table style={{ width: '100%', minWidth: supplyTab === 'vendor' ? '720px' : '820px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {supplyTab === 'vendor' ? (
                          <><th style={thFirst}>거래처</th><th style={{ ...th, textAlign: 'right' }}>건수</th><th style={{ ...th, textAlign: 'right' }}>공급가</th><th style={{ ...th, textAlign: 'right' }}>부가세</th><th style={{ ...th, textAlign: 'right' }}>합계액</th><th style={{ ...th, textAlign: 'right' }}>비중(%)</th></>
                        ) : (
                          <><th style={thFirst}>품번</th><th style={th}>품명</th><th style={th}>규격</th><th style={{ ...th, textAlign: 'right' }}>수량합</th><th style={{ ...th, textAlign: 'right' }}>합계액</th><th style={{ ...th, textAlign: 'right' }}>비중(%)</th></>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {supplyGroups.map((group) => (
                        <Fragment key={group.key || '(empty)'}>
                          <tr className="gw-tr">
                            {supplyTab === 'vendor' ? (
                              <>
                                <td style={tdFirst}><button type="button" aria-expanded={expandedKey === group.key} onClick={() => toggleDetails(group.key)} style={{ border: 'none', background: 'none', padding: 0, color: COLORS.primaryDark, cursor: 'pointer', textAlign: 'left', fontWeight: 600 }}>{expandedKey === group.key ? <IconChevronDown size={14} stroke={ICON_STROKE} aria-hidden="true" style={chevron} /> : <IconChevronRight size={14} stroke={ICON_STROKE} aria-hidden="true" style={chevron} />}{group.label || '(거래처 없음)'}</button></td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNumber(group.count)}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNumber(group.supply_amount)}원</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNumber(group.vat)}원</td>
                                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmtNumber(group.total_amount)}원</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNumber(group.ratio)}</td>
                              </>
                            ) : (
                              <>
                                <td style={tdFirst}><button type="button" aria-expanded={expandedKey === group.key} onClick={() => toggleDetails(group.key)} style={{ border: 'none', background: 'none', padding: 0, color: COLORS.primaryDark, cursor: 'pointer', textAlign: 'left', fontWeight: 600 }}>{expandedKey === group.key ? <IconChevronDown size={14} stroke={ICON_STROKE} aria-hidden="true" style={chevron} /> : <IconChevronRight size={14} stroke={ICON_STROKE} aria-hidden="true" style={chevron} />}{group.item_code || '(품번 없음)'}</button></td>
                                <td style={td}>{group.item_name || '-'}</td>
                                <td style={td}>{group.spec || '-'}</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNumber(group.qty)}</td>
                                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{fmtNumber(group.total_amount)}원</td>
                                <td style={{ ...td, textAlign: 'right' }}>{fmtNumber(group.ratio)}</td>
                              </>
                            )}
                          </tr>
                          {expandedKey === group.key && (
                            <tr>
                              <td colSpan={6} style={{ padding: '10px 12px 14px', background: TONES.gray.soft, borderBottom: `1px solid ${COLORS.borderSoft}` }}>
                                {detailError ? (
                                  <div role="alert" style={{ padding: '12px', color: COLORS.dangerText }}>{detailError}</div>
                                ) : detailLoading && detailRows.length === 0 ? (
                                  <div style={{ padding: '12px', color: COLORS.textMuted }}>원본 명세를 불러오는 중…</div>
                                ) : (
                                  <div style={{ overflowX: 'auto' }}>
                                    <table aria-label={`${group.label || group.key} 원본 명세`} style={{ width: '100%', minWidth: '980px', borderCollapse: 'collapse' }}>
                                      <thead><tr><th style={thFirst}>입고일자</th><th style={th}>입고번호</th><th style={th}>거래처</th><th style={th}>품번</th><th style={th}>품명</th><th style={th}>규격</th><th style={{ ...th, textAlign: 'right' }}>수량</th><th style={{ ...th, textAlign: 'right' }}>단가</th><th style={{ ...th, textAlign: 'right' }}>합계액</th></tr></thead>
                                      <tbody>
                                        {detailRows.map((detail) => (
                                          <tr key={detail.id} className="gw-tr"><td style={tdFirst}>{detail.recv_date}</td><td style={td}>{detail.recv_no}</td><td style={td}>{detail.vendor}</td><td style={td}>{detail.item_code || '-'}</td><td style={td}>{detail.item_name || '-'}</td><td style={td}>{detail.spec || '-'}</td><td style={{ ...td, textAlign: 'right' }}>{fmtNumber(detail.qty)} {detail.unit}</td><td style={{ ...td, textAlign: 'right' }}>{fmtNumber(detail.unit_price)}원</td><td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmtNumber(detail.total_amount)}원</td></tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {detailHasMore && (
                                      <Button variant="quiet" disabled={detailLoading} onClick={() => loadDetails(group.key, true)} style={{ display: 'block', margin: '10px auto 0' }}>
                                        {detailLoading ? '불러오는 중…' : `명세 더 보기 (${detailRows.length} / ${detailTotal})`}
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      ))}
                    </tbody>
                    <tfoot style={{ position: 'sticky', bottom: 0, background: TONES.gray.soft }}>
                      <tr>
                        {supplyTab === 'vendor' ? (
                          <><td style={tdFirst}>합계</td><td style={{ ...td, textAlign: 'right', fontWeight: 500 }}>{fmtNumber(supplyTotals.count)}</td><td style={{ ...td, textAlign: 'right', fontWeight: 500 }}>{fmtNumber(supplyTotals.supply_amount)}원</td><td style={{ ...td, textAlign: 'right', fontWeight: 500 }}>{fmtNumber(supplyTotals.vat)}원</td><td style={{ ...td, textAlign: 'right', fontWeight: 500 }}>{fmtNumber(supplyTotals.total_amount)}원</td><td style={{ ...td, textAlign: 'right', fontWeight: 500 }}>100</td></>
                        ) : (
                          <><td colSpan={3} style={tdFirst}>합계</td><td style={{ ...td, textAlign: 'right', fontWeight: 500 }}>{fmtNumber(supplyTotals.qty)}</td><td style={{ ...td, textAlign: 'right', fontWeight: 500 }}>{fmtNumber(supplyTotals.total_amount)}원</td><td style={{ ...td, textAlign: 'right', fontWeight: 500 }}>100</td></>
                        )}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </section>
            </Card>

            {/* ── 등록 원본 조회 ── */}
            <SectionTitle title="등록 원본" />
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
              <SearchInput width={240} value={q} onChange={(e) => setQ(e.target.value)} placeholder="파일명·메모·작성자 검색" />
              <span style={{ fontSize: '13px', color: COLORS.textSub }}>기간</span>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={input} />
              <span style={{ color: COLORS.textMuted }}>~</span>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={input} />
              {(start || end || q) && (
                <Button variant="secondary" onClick={() => { setStart(''); setEnd(''); setQ(''); }}>초기화</Button>
              )}
              <span style={{ fontSize: '13px', color: COLORS.textSub }}>{shown.length} / {entries.length}건</span>
            </div>

            {loading ? (
              <div style={{ padding: '30px', color: COLORS.textMuted, fontSize: '14px' }}>불러오는 중…</div>
            ) : rows.length === 0 ? (
              <EmptyState description={entries.length ? '조건에 맞는 자료가 없습니다.' : '아직 등록된 자료가 없습니다.'} />
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                {/* 고정폭 — 첨부가 여러 개인 행이 섞여도 열 너비가 흔들리지 않게 */}
                <colgroup>
                  <col style={{ width: '110px' }} />
                  <col />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '110px' }} />
                  <col style={{ width: '84px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={thFirst}>일자</th>
                    <th style={th}>첨부</th>
                    <th style={th}>메모</th>
                    <th style={th}>작성자</th>
                    <th style={th} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.id} className="gw-tr">
                      <td style={{ ...tdFirst, verticalAlign: 'top', whiteSpace: 'nowrap' }}>{e.cost_date}</td>
                      <td style={tdTop}>
                        {(e.files || []).map((f) => (
                          <div key={f.id} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '3px' }}>
                            <button onClick={() => download(f)}
                              style={{ border: 'none', background: 'none', padding: 0, color: COLORS.primaryDark, cursor: 'pointer', fontSize: '13px', textAlign: 'left', textDecoration: 'underline' }}>
                              {f.name}
                            </button>
                            {f.cost_type && (
                              <span style={{ fontSize: '10px', fontWeight: 500, padding: '1px 6px', borderRadius: '5px', background: COLORS.primarySoft, color: TONES.blue.ink, whiteSpace: 'nowrap' }}>
                                {TYPE_LABEL[f.cost_type] || f.cost_type}
                              </span>
                            )}
                            <span style={{ fontSize: '11px', color: COLORS.textMuted, whiteSpace: 'nowrap' }}>{fmtSize(f.size)}</span>
                          </div>
                        ))}
                      </td>
                      <td style={tdTop}>{e.note || '-'}</td>
                      <td style={{ ...tdTop, whiteSpace: 'nowrap' }}>{e.uploader_name}</td>
                      <td style={tdTop}>
                        <Button variant="danger" onClick={() => remove(e)}>삭제</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!loading && shown.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', marginTop: '16px', flexWrap: 'wrap' }}>
                <Button variant="secondary" icon={<IconChevronsLeft aria-hidden="true" />} aria-label="첫 쪽" onClick={() => setPage(1)} disabled={curPage <= 1} />
                <Button variant="secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={curPage <= 1}>이전</Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPages || Math.abs(n - curPage) <= 2)
                  .reduce((acc, n) => { const p = acc[acc.length - 1]; if (p && n - p > 1) acc.push('…'); acc.push(n); return acc; }, [])
                  .map((n, i) => (n === '…'
                    ? <span key={'e' + i} style={{ color: COLORS.textMuted, fontSize: '13px' }}>…</span>
                    : <Button key={n} variant={n === curPage ? 'primary' : 'secondary'} onClick={() => setPage(n)} style={{ minWidth: '34px' }}>{n}</Button>))}
                <Button variant="secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={curPage >= totalPages}>다음</Button>
                <Button variant="secondary" icon={<IconChevronsRight aria-hidden="true" />} aria-label="마지막 쪽" onClick={() => setPage(totalPages)} disabled={curPage >= totalPages} />
                <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }} style={{ ...input, padding: '5px 8px', fontSize: '13px' }}>
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}개씩</option>)}
                </select>
              </div>
            )}
          </>
        )}
      </Page>
      </div>
    </AppShell>
  );
}
